import React, { useState } from "react";
import { useModifyPaymentCheck } from "@/hooks/useModifyPaymentCheck";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

interface PaymentCheckRowActionsProps {
  checkId: string;
  status: "CREATED" | "PAID";
  batchStatus: "DRAFT" | "FINALIZED";
}

const PaymentCheckRowActions: React.FC<PaymentCheckRowActionsProps> = ({
  checkId,
  status,
  batchStatus,
}) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editAmount, setEditAmount] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const {
    mutate: modifyCheck,
    status: mutationStatus,
    error,
  } = useModifyPaymentCheck(checkId);

  if (batchStatus === "FINALIZED") {
    return <span className="text-xs text-muted">Locked</span>;
  }

  const handleEditSubmit = () => {
    if (editAmount.trim()) {
      modifyCheck({ amount: editAmount, notes: editNotes });
      setShowEditModal(false);
      setEditAmount("");
      setEditNotes("");
    }
  };

  return (
    <div className="flex gap-2">
      <Button
        variant="outline"
        className="btn btn-xs hover:text-primary-foreground"
        onClick={() => setShowEditModal(true)}
        disabled={mutationStatus === "pending"}
      >
        Edit
      </Button>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Payment Check</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground mb-2">
              <b>Note:</b> This is an{" "}
              <span className="text-warning font-semibold">ADJUSTMENT</span> and
              will not set the final amount of the check. It will adjust the
              total amount by the value you enter below.
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Adjustment Amount
              </label>
              <Input
                type="number"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
                placeholder="Enter adjustment amount"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <Input
                type="text"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Enter notes (optional)"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleEditSubmit}
              disabled={mutationStatus === "pending" || !editAmount.trim()}
            >
              Submit
            </Button>
          </DialogFooter>
          {error && (
            <span className="text-xs text-destructive mt-2">
              {error.message}
            </span>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentCheckRowActions;
